import { noop } from 'lodash/fp';
import { useCallback, useRef } from 'react';

import useToggle from 'lib/hooks/useToggle';

interface UseConfirmationCallbacks {
  prompt: (onConfirm: () => void, onCancel?: () => void) => Promise<void>;
  confirm: () => void;
  cancel: () => void;
}

/**
 * useConfirmation
 *
 * Promise-based confirmation callback. Can be used for confirmation modals.
 * Calling `prompt()` will set `isPending` to true and return a promise that is
 * resolved when `confirm()` is called, and rejected when `cancel()` is called.
 */
export default function (initialValue = false): [boolean, UseConfirmationCallbacks] {
  const [isPending, { enable: pendingStart, disable: pendingEnd }] = useToggle(initialValue);

  const confirmRef = useRef<() => void>(noop);
  const cancelRef = useRef<() => void>(noop);

  const prompt = useCallback(async (onConfirm: () => void, onCancel: () => void = noop) => {
    pendingStart();

    try {
      await new Promise<void>((res, rej) => {
        confirmRef.current = res;
        cancelRef.current = rej;
      });
      onConfirm();
    } catch {
      onCancel();
    } finally {
      pendingEnd();
    }
  }, []);

  return [isPending, { prompt, confirm: confirmRef.current, cancel: cancelRef.current }];
}
